import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApiService } from '../../shared/auth/services/authApi';
import { ENV } from '../../config/env';

interface PasswordData {
    password: string;
    confirmPassword: string;
}

interface FieldErrors {
    password?: string;
    confirmPassword?: string;
}

const AdminSetupPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [passwordData, setPasswordData] = useState<PasswordData>({
        password: '',
        confirmPassword: '',
    });
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [error, setError] = useState('');
    const [tokenValid, setTokenValid] = useState(false);

    // Extract encrypted token from URL
    const encryptedToken = searchParams.get('token') || '';
    const [decryptedToken, setDecryptedToken] = useState<string>('');

    // Decrypt and verify token on component mount
    useEffect(() => {
        const decryptAndVerifyToken = async () => {

            if (!encryptedToken) {
                setError('Invalid setup link. Please check your email for the correct link.');
                setIsVerifying(false);
                return;
            }

            try {
                // Step 1: Decrypt the token
                const decryptResponse = await fetch(`${ENV.API_BASE_URL}/setup/v1/token/decrypt`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        encryptedToken: encryptedToken,
                    }),
                });

                const decryptData = await decryptResponse.json();

                if (decryptData.status.error) {
                    setError('Invalid or corrupted setup link. Please request a new one.');
                    setTokenValid(false);
                    setIsVerifying(false);
                    return;
                }

                const plainToken = decryptData.payload.token;
                setDecryptedToken(plainToken);

                // Step 2: Validate the decrypted token with backend
                const response = await fetch(`${ENV.API_BASE_URL}/setup/v1/token/${encodeURIComponent(plainToken)}/validate`);
                const data = await response.json();

                if (data.status.error) {
                    setError(data.status.msg || 'This setup link is invalid or has expired. Please contact support.');
                    setTokenValid(false);
                } else {
                    setTokenValid(true);
                }
            } catch (err: any) {
                console.error('❌ DEBUG: Token processing failed:', err);
                setError('Failed to process setup link. Please try again or contact support.');
                setTokenValid(false);
            } finally {
                setIsVerifying(false);
            }
        };

        decryptAndVerifyToken();
    }, [encryptedToken]);

    const handleInputChange = (field: keyof PasswordData, value: string) => {
        setPasswordData(prev => ({
            ...prev,
            [field]: value,
        }));

        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }

        if (error) {
            setError('');
        }
    };

    const validateForm = (): boolean => {
        const errors: FieldErrors = {};

        if (!passwordData.password) {
            errors.password = 'Password is required';
        } else if (passwordData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.password)) {
            errors.password = 'Password must contain uppercase, lowercase, and number';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (passwordData.password !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await authApiService.setupPassword(decryptedToken, passwordData.password);

            // Redirect to admin login with success message
            navigate('/auth/login', {
                state: {
                    message: 'Your admin password has been set successfully! You can now log in.'
                }
            });
        } catch (err: any) {
            console.error('❌ DEBUG: Admin password setup failed:', err);
            setError(err.message || 'Failed to set password. Please try again or contact support.');
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading while verifying token
    if (isVerifying) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: '#015FA6' }}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white text-lg">Verifying setup link...</p>
                    <p className="text-white text-sm mt-2 opacity-90">Please wait while we validate your admin invitation</p>
                </div>
            </div>
        );
    }

    // Show error if token is invalid
    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: '#015FA6' }}>
                <div className="text-center max-w-md p-8">
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                        <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Setup Link</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <p className="text-sm text-gray-500 mb-6">
                            If you need assistance, please contact the platform administrator.
                        </p>
                        <button
                            onClick={() => navigate('/auth/login')}
                            className="px-6 py-3 bg-[#015FA6] text-white rounded-lg font-medium hover:bg-[#014a85] transition-colors"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-start justify-center relative overflow-hidden pt-16"
            style={{
                background: '#015FA6',
                fontFamily: 'Manrope, sans-serif'
            }}
        >
            {/* Background decorative ellipse */}
            <div
                style={{
                    position: 'absolute',
                    width: '1078.81px',
                    height: '1064.85px',
                    left: '-514px',
                    bottom: '-209.85px',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                    transform: 'rotate(-153.16deg)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}
            />

            {/* Main content container */}
            <div className="w-full max-w-[480px] px-4 py-6 relative z-10">
                {/* Logo */}
                <div className="text-center mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        className="mx-auto cursor-pointer"
                        style={{
                            width: 'auto',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                                parent.innerHTML = '<div style="font-size: 32px; font-weight: 700; color: white; font-family: Manrope;">Hush</div>';
                            }
                        }}
                    />
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '32px',
                            lineHeight: '100%',
                            color: '#FFFFFF',
                            marginBottom: '12px'
                        }}
                        className="text-2xl sm:text-3xl"
                    >
                        Set Your Admin Password
                    </h1>
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            fontSize: '14px',
                            lineHeight: '150%',
                            textAlign: 'center',
                            color: '#FFFFFF'
                        }}
                        className="text-sm"
                    >
                        Welcome to Hush Admin. Create a secure password to access your dashboard.
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* New Password Field */}
                    <div>
                        <label
                            htmlFor="password"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: '#FFFFFF',
                                display: 'block',
                                marginBottom: '6px'
                            }}
                        >
                            New password
                        </label>
                        <div className="relative">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={passwordData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="@#*%"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    height: '45px',
                                    padding: '0 50px 0 14px',
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.154px',
                                    color: 'rgba(1, 95, 166, 0.5)',
                                    background: '#F7FAFC',
                                    border: fieldErrors.password ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    borderRadius: '10px',
                                    outline: 'none'
                                }}
                                className="w-full"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={isLoading}
                                style={{
                                    position: 'absolute',
                                    right: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    {showPassword ? (
                                        <path
                                            d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"
                                            stroke="rgba(1, 95, 166, 0.5)"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    ) : (
                                        <>
                                            <path
                                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="3"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                            />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        {fieldErrors.password && (
                            <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {fieldErrors.password}
                            </p>
                        )}
                    </div>

                    {/* Confirm New Password Field */}
                    <div>
                        <label
                            htmlFor="confirmPassword"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: '#FFFFFF',
                                display: 'block',
                                marginBottom: '6px'
                            }}
                        >
                            Confirm new password
                        </label>
                        <div className="relative">
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={passwordData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                placeholder="@#*%"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    height: '45px',
                                    padding: '0 50px 0 14px',
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.154px',
                                    color: 'rgba(1, 95, 166, 0.5)',
                                    background: '#F7FAFC',
                                    border: fieldErrors.confirmPassword ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    borderRadius: '10px',
                                    outline: 'none'
                                }}
                                className="w-full"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={isLoading}
                                style={{
                                    position: 'absolute',
                                    right: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    {showConfirmPassword ? (
                                        <path
                                            d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"
                                            stroke="rgba(1, 95, 166, 0.5)"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    ) : (
                                        <>
                                            <path
                                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="3"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                            />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        {fieldErrors.confirmPassword && (
                            <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {fieldErrors.confirmPassword}
                            </p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !passwordData.password.trim() || !passwordData.confirmPassword.trim()}
                        style={{
                            width: '100%',
                            height: '45px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '22px',
                            color: '#015FA6',
                            background: '#F8F9FB',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (isLoading || !passwordData.password.trim() || !passwordData.confirmPassword.trim()) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            marginTop: '16px',
                            opacity: (!passwordData.password.trim() || !passwordData.confirmPassword.trim()) ? 0.6 : 1
                        }}
                        className="hover:shadow-lg hover:bg-white"
                    >
                        {isLoading ? 'Creating password...' : 'Create Admin Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminSetupPassword;
